import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, ShieldCheck, Loader2, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BillingApprovals = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [actingId, setActingId] = useState(null);

  const fetchData = async () => {
    try {
      const res = await api.get('/admin/billing/pending-approvals');
      setClients(res.data.clients || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const act = async (userId, approved) => {
    const note = window.prompt(`Optional note for this ${approved ? 'approval' : 'denial'} (press OK to skip):`, '') || '';
    setActingId(userId);
    try {
      await api.patch(`/admin/billing/pending-approvals/${userId}`, { approved, note });
      toast.success(approved ? 'Terms approved' : 'Terms denied — reverted to due_on_receipt');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActingId(null); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50/30 to-white">
      <div className="max-w-5xl mx-auto px-3 sm:px-6 py-5 space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/home')} data-testid="approvals-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Home
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-amber-500" /> Pending Payment Approvals
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Clients that requested Net 15 / Net 30 terms. Approve to unlock net-terms billing, or deny to revert to Due on Receipt.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="py-12 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>
        ) : clients.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-sm text-gray-500">
              <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
              No pending payment-term approvals.
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" /> Requests ({clients.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 divide-y divide-gray-100">
              {clients.map((c) => (
                <div key={c.user_id} className="px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`approval-row-${c.user_id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">{c.company_name || c.email}</p>
                    <p className="text-[11px] text-gray-500">{c.email}</p>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      <Badge className="text-[10px] bg-amber-100 text-amber-700 border border-amber-200">{c.payment_terms.replace('_', ' ')}</Badge>
                      {c.client_type && <Badge variant="outline" className="text-[10px] capitalize">{c.client_type}</Badge>}
                      {c.billing_model && <Badge variant="outline" className="text-[10px]">{c.billing_model.replace('_', ' ')}</Badge>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => act(c.user_id, true)}
                      disabled={actingId === c.user_id}
                      className="bg-emerald-500 hover:bg-emerald-600 text-white"
                      data-testid={`approve-btn-${c.user_id}`}
                    >
                      {actingId === c.user_id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => act(c.user_id, false)}
                      disabled={actingId === c.user_id}
                      className="text-red-600 hover:bg-red-50"
                      data-testid={`deny-btn-${c.user_id}`}
                    >
                      <XCircle className="w-4 h-4 mr-1" /> Deny
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default BillingApprovals;
